import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Ruler, Glasses } from "lucide-react";
import { money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useBrandLogos } from "@/lib/brandLogos";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ConditionMeter } from "@/components/ConditionRating";
import { getFitLabel } from "@/lib/fit";

export function DiscountBadge({ pct, amount }) {
  if (!pct || pct <= 0) return null;
  return (
    <span className="inline-flex items-center gap-1.5 bg-[#09B8DF] text-charcoal text-xs font-heading font-extrabold px-2.5 py-1.5 rounded-md shadow-sm" data-testid="discount-badge">
      Save {amount != null ? `${money(amount)} · ` : ""}{pct}% Off
    </span>
  );
}

export function ProductCard({ product, wishlisted = false, onWishlistChange, fitReference = null }) {
  const { user } = useAuth();
  const { items, addToCart } = useCart();
  const { logoFor } = useBrandLogos();
  const navigate = useNavigate();
  const [saved, setSaved] = useState(wishlisted);
  const [hover, setHover] = useState(false);
  const img = product.images?.[0]?.url;
  const img2 = product.images?.[1]?.url || img;
  const inCart = items.some((i) => i.product_id === product.id);
  const rrpVerified = product.rrp_verified !== false && !!product.original_rrp;
  const savingsAmount = rrpVerified ? Math.round(product.original_rrp - product.selling_price) : null;
  const savingsPct = rrpVerified ? Math.round(((product.original_rrp - product.selling_price) / product.original_rrp) * 100) : null;
  const logoUrl = logoFor(product.brand_slug);
  const fitLabel = getFitLabel(product, fitReference);

  const toggleWishlist = async (e) => {
    e.preventDefault();
    if (!user) {
      toast("Log in to save favourites");
      return;
    }
    try {
      if (saved) {
        await api.delete(`/wishlist/${product.id}`);
      } else {
        await api.post(`/wishlist/${product.id}`);
      }
      setSaved(!saved);
      onWishlistChange && onWishlistChange(product.id, !saved);
      toast(saved ? "Removed from wishlist" : "Saved to wishlist");
    } catch {
      toast("Could not update wishlist");
    }
  };

  const handleTryOn = (e) => {
    e.preventDefault();
    navigate(`/try-on/${product.id}`);
  };

  const handleQuickAdd = (e) => {
    e.preventDefault();
    if (product.status !== "live" || inCart) return;
    addToCart(product);
    toast.success(`${product.brand_name} ${product.model} added to your bag`);
  };

  return (
    <Link
      to={`/product/${product.slug || product.id}`}
      className="group flex flex-col"
      data-testid={`product-card-${product.id}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="relative aspect-[4/3] bg-[#F2F2F4] border border-neutral-200/70 rounded-2xl overflow-hidden mb-4 transition-colors duration-300 group-hover:border-teal-500/50">
        <button
          onClick={toggleWishlist}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm shadow-sm flex items-center justify-center text-neutral-500 hover:text-rose-500 transition-colors"
          aria-label="Save to wishlist"
          data-testid={`wishlist-toggle-${product.id}`}
        >
          <Heart size={15} className={saved ? "fill-rose-500 text-rose-500" : ""} />
        </button>
        <div className="absolute top-3 left-3 z-10">
          <DiscountBadge pct={savingsPct} amount={savingsAmount} />
        </div>
        <img src={hover ? img2 : img} alt={`${product.brand_name} ${product.model}`} className="w-full h-full object-contain scale-125 transition-transform duration-500 group-hover:scale-[1.35]" loading="lazy" />
        {product.status === "sold" && (
          <div className="absolute inset-0 bg-white/90 flex items-center justify-center rounded-2xl">
            <span className="font-mono text-[11px] uppercase tracking-wider text-neutral-600">This Pair Found A New Home</span>
          </div>
        )}
        {product.status === "live" && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-3 z-10 flex items-center gap-2 transition-all duration-300 opacity-100 translate-y-0 lg:opacity-0 lg:translate-y-2 lg:group-hover:opacity-100 lg:group-hover:translate-y-0">
            <button
              onClick={handleQuickAdd}
              disabled={inCart}
              className={`whitespace-nowrap text-center text-[11px] font-heading font-bold uppercase tracking-wide px-4 sm:px-5 py-2.5 rounded-full shadow-md transition-transform duration-200 hover:scale-105 active:scale-95 ${inCart ? "bg-teal-600 text-white" : "bg-charcoal text-white"}`}
              data-testid={`quick-add-${product.id}`}
            >
              {inCart ? "In Your Bag" : "Quick Add"}
            </button>
            {product.try_on_asset_url && (
              <button
                onClick={handleTryOn}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-[11px] font-heading font-bold uppercase tracking-wide px-3.5 sm:px-4 py-2.5 rounded-full shadow-md bg-white text-charcoal transition-transform duration-200 hover:scale-105 active:scale-95"
                data-testid={`try-on-${product.id}`}
              >
                <Glasses size={13} /> Try It On
              </button>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 mb-1.5">
        {logoUrl ? (
          <img src={logoUrl} alt={product.brand_name} className="h-5 w-auto object-contain max-w-[110px]" />
        ) : (
          <span className="text-sm sm:text-base font-extrabold tracking-widest uppercase text-neutral-900">{product.brand_name}</span>
        )}
      </div>
      <div className="text-sm text-neutral-600 truncate mb-2">{product.model}</div>
      <div className="flex items-baseline gap-2 mb-2 flex-wrap">
        <span className="text-2xl sm:text-3xl font-mono font-extrabold text-neutral-900" data-testid={`card-price-${product.id}`}>{money(product.selling_price)}</span>
        {rrpVerified && <span className="text-sm font-mono text-neutral-400 line-through">{money(product.original_rrp)}</span>}
        {savingsPct > 0 && <span className="text-[11px] font-mono font-bold uppercase text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full">{savingsPct}% Off</span>}
      </div>
      <div className="flex items-center justify-between">
        <ConditionMeter score={product.condition_score} label={product.condition_label} size="md" />
      </div>
      {fitLabel && (
        <div className={`flex items-center gap-1 mt-1.5 text-[11px] font-heading font-bold uppercase tracking-wide ${fitLabel.includes("Match") || fitLabel.includes("Similar") ? "text-teal-600" : "text-neutral-400"}`} data-testid={`fit-indicator-${product.id}`}>
          <Ruler size={11} /> {fitLabel}
        </div>
      )}
    </Link>
  );
}
